// ─────────────────────────────────────────────────────────────────
// Glass-i — Glass Concepts Master page (Stage 6B.8A-3I).
//
// Ready-Made Glass Concept gallery + table. Operator picks a
// concept, taps "Use", and the UseInQuotationDrawer prepares
// quotation lines (BOQ). Concept management (CRUD + duplicate)
// happens via ConceptFormDrawer.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Input, Select, Segmented, Space, Table, Tag, Tooltip,
  Empty, Spin, Popconfirm, message, Row, Col, Switch
} from 'antd';
import {
  PlusOutlined, EditOutlined, EyeOutlined, ToolOutlined,
  CopyOutlined, DeleteOutlined, AppstoreOutlined, BarsOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { glassConceptsApi, CONCEPT_TYPE_OPTIONS } from './glassConceptsApi';
import ConceptGalleryCard      from './components/ConceptGalleryCard';
import ConceptWizard           from './components/ConceptWizard';
import UseInQuotationDrawer    from './components/UseInQuotationDrawer';

function typeLabel(value) {
  const opt = CONCEPT_TYPE_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : value;
}

export default function GlassConceptsPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [view,    setView]    = useState('gallery'); // gallery | table
  const [q,       setQ]       = useState('');
  const [type,    setType]    = useState(undefined);
  const [active,  setActive]  = useState(true);

  // Drawer state
  const [formOpen,     setFormOpen]     = useState(false);
  const [formId,       setFormId]       = useState(null);
  const [formViewOnly, setFormViewOnly] = useState(false);
  const [useOpen,      setUseOpen]      = useState(false);
  const [useId,        setUseId]        = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await glassConceptsApi.list({
        q, conceptType: type || undefined,
        isActive: active === null ? undefined : active,
        limit: 200, offset: 0
      });
      setRows((out && out.rows) || []);
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load concepts.');
    } finally {
      setLoading(false);
    }
  }, [q, type, active]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setFormId(null); setFormViewOnly(false); setFormOpen(true); };
  const openEdit   = (c) => { setFormId(c.id); setFormViewOnly(false); setFormOpen(true); };
  const openView   = (c) => { setFormId(c.id); setFormViewOnly(true);  setFormOpen(true); };
  const openUse    = (c) => { setUseId(c.id); setUseOpen(true); };

  const onDelete = async (c) => {
    try {
      await glassConceptsApi.remove(c.id);
      message.success('Concept deleted.');
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Delete failed.');
    }
  };
  const onDuplicate = async (c) => {
    try {
      const dup = await glassConceptsApi.duplicate(c.id);
      message.success(`Duplicated as ${dup.concept.conceptCode}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Duplicate failed.');
    }
  };

  // ── Table columns ────────────────────────────────────────────
  const tableColumns = [
    { title: 'Code', dataIndex: 'conceptCode', width: 110,
      render: (v) => <code style={{
        fontSize: 11, color: '#475569', background: '#f1f5f9',
        padding: '1px 6px', borderRadius: 3
      }}>{v}</code> },
    { title: 'Concept Name', dataIndex: 'conceptName',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 500, color: '#0f172a' }}>{v}</div>
          {r.description ? (
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {String(r.description).slice(0, 80)}{r.description.length > 80 ? '…' : ''}
            </div>
          ) : null}
        </div>
      ) },
    { title: 'Type', dataIndex: 'conceptType', width: 170,
      render: (v) => <Tag color="geekblue" style={{ margin: 0 }}>{typeLabel(v)}</Tag> },
    { title: 'Category', dataIndex: 'designCategory', width: 140,
      render: (v) => v || <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Default Size', width: 130,
      render: (_v, r) => (r.defaultWidthMm && r.defaultHeightMm)
        ? <span style={{ fontSize: 12 }}>{r.defaultWidthMm} × {r.defaultHeightMm} mm</span>
        : <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Panels',    dataIndex: 'panelCount',    width: 70, align: 'center' },
    { title: 'Hardware',  dataIndex: 'hardwareCount', width: 80, align: 'center' },
    { title: 'Active', dataIndex: 'isActive', width: 70, align: 'center',
      render: (v) => v
        ? <Tag color="green" style={{ margin: 0 }}>Yes</Tag>
        : <Tag color="default" style={{ margin: 0 }}>No</Tag> },
    { title: 'Actions', width: 200, align: 'right',
      render: (_v, r) => (
        <Space size={2}>
          <Tooltip title="View"><Button size="small" type="text"
            icon={<EyeOutlined />} onClick={() => openView(r)} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" type="text"
            icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          <Tooltip title="Use in Quotation"><Button size="small" type="primary"
            icon={<ToolOutlined />} onClick={() => openUse(r)}>Use</Button></Tooltip>
          <Tooltip title="Duplicate"><Button size="small" type="text"
            icon={<CopyOutlined />} onClick={() => onDuplicate(r)} /></Tooltip>
          <Popconfirm title="Delete this concept?" okText="Delete" okType="danger"
            onConfirm={() => onDelete(r)}>
            <Tooltip title="Delete"><Button size="small" type="text" danger
              icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ) }
  ];

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: '#0f172a' }}>
          Concept Studio
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          Ready-made architectural glass models with standard glass panels and hardware BOQ.
        </div>
      </div>

      {/* Toolbar */}
      <Card size="small" style={{ marginBottom: 12 }}
        styles={{ body: { padding: '10px 12px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New Concept
          </Button>
          <Input.Search allowClear placeholder="Search code, name, category…"
            value={q} onChange={(e) => setQ(e.target.value)}
            onSearch={() => load()}
            style={{ width: 280 }} />
          <Select allowClear value={type} placeholder="All types"
            options={CONCEPT_TYPE_OPTIONS} style={{ width: 220 }}
            onChange={(v) => setType(v)} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Switch size="small" checked={active === true}
              onChange={(c) => setActive(c ? true : null)} />
            <span style={{ fontSize: 12, color: '#475569' }}>Active only</span>
          </span>
          <Button icon={<ReloadOutlined />} onClick={() => load()} />
          <div style={{ marginLeft: 'auto' }}>
            <Segmented
              size="small"
              value={view}
              onChange={(v) => setView(v)}
              options={[
                { value: 'gallery', icon: <AppstoreOutlined />, label: 'Gallery' },
                { value: 'table',   icon: <BarsOutlined />,     label: 'Table' }
              ]}
            />
          </div>
        </div>
      </Card>

      {/* Body */}
      <Spin spinning={loading}>
        {rows.length === 0 ? (
          <Card><Empty description="No glass concepts yet. Click New Concept to start." /></Card>
        ) : view === 'gallery' ? (
          <Row gutter={[12, 12]}>
            {rows.map((c) => (
              <Col xs={24} sm={12} md={8} lg={6} xl={6} key={c.id}>
                <ConceptGalleryCard
                  concept={c}
                  onView={openView}
                  onEdit={openEdit}
                  onUse={openUse}
                  onDuplicate={onDuplicate}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <Card size="small" styles={{ body: { padding: 0 } }}>
            <Table size="small" rowKey="id"
              dataSource={rows} columns={tableColumns}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              scroll={{ x: 'max-content' }}
              onRow={(record) => ({ onDoubleClick: () => openView(record) })}
            />
          </Card>
        )}
      </Spin>

      <ConceptWizard
        open={formOpen}
        conceptId={formId}
        viewOnly={formViewOnly}
        onClose={() => setFormOpen(false)}
        onSaved={() => load()}
      />
      <UseInQuotationDrawer
        open={useOpen}
        conceptId={useId}
        onClose={() => setUseOpen(false)}
      />
    </div>
  );
}
